/**
 * GuestIdentityDrawer — step di identità per checkout wa_redirect.
 *
 * Chiede nome, cognome (opzionale) e numero WhatsApp al cliente prima
 * di confermare l'ordine. Al submit chiama onConfirm({ name, surname, phone }).
 *
 * Props:
 *   open          {boolean}
 *   onClose       {function}
 *   primaryColor  {string}
 *   submitting    {boolean}
 *   submitError   {string}
 *   onConfirm     {function({ name, surname, phone })}
 */

import { useEffect, useState } from "react";
import { AlertCircle, LoaderCircle, X } from "lucide-react";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "./ui/drawer.jsx";

const EMPTY = { name: "", surname: "", phone: "" };

const COUNTRY_PREFIXES = [
  { code: "+39", flag: "🇮🇹", label: "IT" },
  { code: "+44", flag: "🇬🇧", label: "UK" },
  { code: "+33", flag: "🇫🇷", label: "FR" },
  { code: "+49", flag: "🇩🇪", label: "DE" },
  { code: "+34", flag: "🇪🇸", label: "ES" },
  { code: "+41", flag: "🇨🇭", label: "CH" },
  { code: "+31", flag: "🇳🇱", label: "NL" },
  { code: "+32", flag: "🇧🇪", label: "BE" },
  { code: "+1",  flag: "🇺🇸", label: "US" },
];

export default function GuestIdentityDrawer({
  open,
  onClose,
  primaryColor = "#0f172a",
  submitting = false,
  submitError = "",
  onConfirm,
}) {
  const [form, setForm] = useState(EMPTY);
  const [prefix, setPrefix] = useState("+39");
  const [error, setError] = useState("");

  // Reset every time the drawer opens
  useEffect(() => {
    if (!open) return;
    setForm(EMPTY);
    setPrefix("+39");
    setError("");
  }, [open]);

  function setField(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
    setError("");
  }

  function handleConfirm() {
    if (!form.name.trim()) {
      setError("Inserisci il tuo nome.");
      return;
    }
    const rawPhone = form.phone.trim();
    if (!rawPhone) {
      setError("Inserisci il tuo numero WhatsApp.");
      return;
    }

    const fullPhone = rawPhone.startsWith("+") ? rawPhone : `${prefix}${rawPhone.replace(/^0/, "")}`;
    setError("");
    onConfirm({
      name: form.name.trim(),
      surname: form.surname.trim(),
      phone: fullPhone,
    });
  }

  const displayError = error || submitError;

  return (
    <Drawer open={open} onOpenChange={(v) => { if (!v && !submitting) onClose(); }}>
      <DrawerContent className="mx-auto w-full max-w-[800px] rounded-t-2xl border-[#e2e8f0] bg-white max-h-[90vh]">
        <DrawerHeader className="relative px-5 pt-5 pb-3">
          <DrawerClose asChild>
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-500 transition hover:bg-slate-200 disabled:opacity-40"
            >
              <X className="h-4 w-4" />
            </button>
          </DrawerClose>
          <DrawerTitle className="text-[18px] font-bold text-slate-900">
            I tuoi dati
          </DrawerTitle>
          <DrawerDescription className="mt-1 text-sm text-slate-500">
            Inserisci nome e numero WhatsApp per completare l&apos;ordine.
          </DrawerDescription>
        </DrawerHeader>

        <div className="overflow-y-auto px-5 pb-5" style={{ maxHeight: "65vh" }}>
          <div className="space-y-4">
            {/* Nome + Cognome */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  Nome *
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setField("name", e.target.value)}
                  placeholder="Mario"
                  autoComplete="given-name"
                  className="h-11 w-full rounded-2xl border border-[#d1dde8] bg-[#f8fbff] px-4 text-sm text-[#0f172a] outline-none transition placeholder:text-[#94a3b8] focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/20"
                  style={{ "--primary": primaryColor }}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  Cognome
                </label>
                <input
                  type="text"
                  value={form.surname}
                  onChange={(e) => setField("surname", e.target.value)}
                  placeholder="Rossi"
                  autoComplete="family-name"
                  className="h-11 w-full rounded-2xl border border-[#d1dde8] bg-[#f8fbff] px-4 text-sm text-[#0f172a] outline-none transition placeholder:text-[#94a3b8] focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/20"
                  style={{ "--primary": primaryColor }}
                />
              </div>
            </div>

            {/* Numero WhatsApp */}
            <div className="space-y-1.5">
              <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Numero WhatsApp *
              </label>
              <div className="flex gap-2">
                <select
                  value={prefix}
                  onChange={(e) => setPrefix(e.target.value)}
                  className="h-11 shrink-0 rounded-2xl border border-[#d1dde8] bg-[#f8fbff] px-3 text-sm text-[#0f172a] outline-none transition focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/20"
                  style={{ "--primary": primaryColor }}
                >
                  {COUNTRY_PREFIXES.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.flag} {c.code}
                    </option>
                  ))}
                </select>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setField("phone", e.target.value)}
                  placeholder="333 123 4567"
                  autoComplete="tel"
                  className="h-11 min-w-0 flex-1 rounded-2xl border border-[#d1dde8] bg-[#f8fbff] px-4 text-sm text-[#0f172a] outline-none transition placeholder:text-[#94a3b8] focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/20"
                  style={{ "--primary": primaryColor }}
                />
              </div>
              <p className="text-[11px] text-slate-400">
                Verrai reindirizzato su WhatsApp dopo la conferma.
              </p>
            </div>

            {/* Error */}
            {displayError && (
              <div className="flex items-start gap-2 rounded-2xl bg-red-50 px-4 py-3 text-sm text-red-600">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{displayError}</span>
              </div>
            )}

            {/* Confirm button */}
            <button
              type="button"
              onClick={handleConfirm}
              disabled={submitting}
              className="mt-2 flex h-14 w-full items-center justify-center gap-2 rounded-2xl font-bold text-white transition disabled:opacity-60"
              style={{ backgroundColor: primaryColor }}
            >
              {submitting ? (
                <>
                  <LoaderCircle className="h-5 w-5 animate-spin" />
                  Invio in corso...
                </>
              ) : (
                "CONFERMA ORDINE"
              )}
            </button>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
